/**
 * Who may reach which routes, and the legacy-URL redirects.
 *
 * Extracted out of `App.tsx`, which used to hold providers, guards and the
 * route table in one 442-line file — `App.tsx` is routes only now.
 *
 * ── TWO GUARDS, AND WHY THEY ARE DIFFERENT ────────────────────────────────
 * `RequireSession` answers "is anybody signed in" — if not, the sign-in screen,
 * with no message, because not being signed in is not an error.
 *
 * `RequirePermission` answers "may this person be here". A typed URL for the
 * other half of the product sends them home rather than showing a screen they
 * cannot use: the two working roles are gated as PLACES, so a data-entry user
 * has no audit destination at all and an auditor has no upload control.
 *
 * Neither guard is security. Both are courtesy. The API enforces the same
 * rules on every call, so removing these would leak nothing — it would only
 * show somebody a screen that then fails to load.
 */

import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../provider/auth';
import {
	MODULE_PARAM,
	readModule,
	withModule,
} from '../features/modules/modules';
import { hubPath, modulePagePath } from '../features/modules/flow';
import { ErrorState, LoadingState } from '../features/shared/states';
import { useAuditQuery } from '../queries/audits';
import type { Permissions } from '../schemas';

/**
 * A redirect that keeps the query string — above all `?module=`, so an old
 * link or a moved route lands in the module it was opened in. `to` may be
 * relative ("analyses", "../details"). `moduleOnly` carries `module` and drops
 * the rest, for a redirect to an unrelated page (a group id means nothing on
 * the home page).
 */
export function NavigateKeepingQuery({
	to,
	moduleOnly = false,
}: {
	to: string;
	moduleOnly?: boolean;
}) {
	const { search } = useLocation();
	const module = new URLSearchParams(search).get(MODULE_PARAM);
	const kept = !moduleOnly
		? search
		: module
			? `?${new URLSearchParams({ [MODULE_PARAM]: module })}`
			: '';
	return <Navigate to={{ pathname: to, search: kept }} replace />;
}

export function RequireSession({ children }: { children: JSX.Element }) {
	const { user, loading, offline, reload } = useAuth();
	const location = useLocation();

	/* Wait for /auth/me before deciding. Without this, a reload flashes the
     sign-in screen for anybody who is already signed in. */
	if (loading) return <LoadingState label="Signing you in…" />;

	/* An API that did not answer is not a person who is not signed in.
     Sending them to /sign-in here is worse than useless: the session cookie is
     valid, so the sign-in screen is the one thing that cannot help, and the
     credentials they retype go nowhere either. The provider is already
     retrying, so this state clears itself. */
	if (!user && offline) {
		return (
			<ErrorState
				error={offline}
				onRetry={() => void reload().catch(() => {})}
				hint="You are still signed in. This page carries on by itself once the server answers."
			/>
		);
	}

	if (!user)
		return <Navigate to="/sign-in" replace state={{ from: location }} />;
	return children;
}

export function RequirePermission({
	need,
	children,
}: {
	need: keyof Permissions;
	children: JSX.Element;
}) {
	const { permissions, loading } = useAuth();
	if (loading) return <LoadingState label="Checking your access…" />;
	if (!permissions?.[need]) return <NavigateKeepingQuery to="/" moduleOnly />;
	return children;
}

/**
 * The audit half, kept whole.
 *
 * `RequirePermission` gates one route on one capability. This gates a WHOLE
 * SUBTREE on being an auditor at all, which is the split the product is built
 * on — and it is a layout route, so it guards every tab of the workspace
 * without each of them repeating the check.
 */
export function RequireAudits() {
	const { permissions, loading } = useAuth();
	if (loading) return <LoadingState label="Checking your access…" />;
	if (!permissions?.accessAudits)
		return <NavigateKeepingQuery to="/" moduleOnly />;
	return <Outlet />;
}

/** The intake half, kept whole — the same shape as `RequireAudits` above, for
 *  the other side of the split. */
export function RequireIntake() {
	const { permissions, loading } = useAuth();
	if (loading) return <LoadingState label="Checking your access…" />;
	if (!permissions?.accessIntake)
		return <NavigateKeepingQuery to="/" moduleOnly />;
	return <Outlet />;
}

/** The shared pages of the Clients menu (list, module page, hub): either half
 *  of the product may open them, so a data-entry user and an auditor both
 *  pass; a user with neither goes home. */
export function RequireIntakeOrAudits() {
	const { permissions, loading } = useAuth();
	if (loading) return <LoadingState label="Checking your access…" />;
	if (!permissions?.accessIntake && !permissions?.accessAudits) {
		return <NavigateKeepingQuery to="/" moduleOnly />;
	}
	return <Outlet />;
}

/** Where signing in lands you, which depends on what you are for. */
export function Home() {
	const { permissions, loading } = useAuth();
	if (loading) return <LoadingState label="Loading…" />;
	/* Each half of the product has its own front door. The dashboard is hidden
     for now, so the audit side starts on the client list — the first entry in
     the sidebar, and where the logo leads. Admins hold `accessAudits` too, so
     they land there alongside auditors rather than needing a third branch. */
	if (permissions?.accessAudits || permissions?.accessIntake) {
		return <NavigateKeepingQuery to="/clients" />;
	}
	if (permissions?.manageUsers)
		return <NavigateKeepingQuery to="/administration" />;
	return <Navigate to="/sign-in" replace />;
}

/**
 * `/clients/:id` — a client, opened from an old link or a typed URL. Anyone
 * with access goes to the client's module page; anyone else goes home.
 */
export function LegacyClientRedirect() {
	const { permissions } = useAuth();
	const { clientId = '' } = useParams();
	if (permissions?.accessAudits || permissions?.accessIntake) {
		return <NavigateKeepingQuery to={modulePagePath(clientId)} />;
	}
	return <NavigateKeepingQuery to="/" moduleOnly />;
}

/** `/clients/:id/home?module=X` — the hub's old URL; the module is in the path
 *  now (`/clients/:id/modules/x`). */
export function LegacyHubRedirect() {
	const { search } = useLocation();
	const { clientId = '' } = useParams();
	return <Navigate to={hubPath(clientId, readModule(search))} replace />;
}

/** `/intake/clients/:id/modules` — the data-entry module page, now the one
 *  module page. */
export function LegacyModulePageRedirect() {
	const { clientId = '' } = useParams();
	return <NavigateKeepingQuery to={modulePagePath(clientId)} />;
}

/**
 * `/intake/clients/:id/…?module=X` — a client's data files moved under the
 * client and module (`/clients/:id/modules/x/data-files/…`). Opened without a
 * module (an old bookmark, a typed URL), the client's module page asks first.
 */
export function LegacyIntakeRedirect() {
	const { pathname, search, hash } = useLocation();
	const { clientId = '' } = useParams();
	if (!new URLSearchParams(search).has(MODULE_PARAM)) {
		return <Navigate to={modulePagePath(clientId)} replace />;
	}
	return (
		<Navigate
			to={withModule(`${pathname}${search}${hash}`, readModule(search))}
			replace
		/>
	);
}

/**
 * `/audits?client=3` and `/audits/new?client=3` moved under the client and
 * module. Without a client they are the firm-wide pages, as before.
 */
export function AuditsForClient({ children }: { children: JSX.Element }) {
	const { pathname, search } = useLocation();
	if (!new URLSearchParams(search).get('client')) return children;
	return (
		<Navigate
			to={withModule(`${pathname}${search}`, readModule(search))}
			replace
		/>
	);
}

/**
 * `/audits/:id/…` — an audit moved under its client and module. The URL does
 * not say which client, so the audit is fetched first.
 */
export function LegacyAuditRedirect() {
	const { auditId = '', '*': rest = '' } = useParams();
	const { search, hash } = useLocation();
	const audit = useAuditQuery(auditId);
	if (audit.isPending) return <LoadingState label="Opening the audit…" />;
	if (!audit.data) return <Navigate to="/clients" replace />;
	const path = `/audits/${auditId}${rest ? `/${rest}` : ''}${search}${hash}`;
	return (
		<Navigate
			to={withModule(path, audit.data.module, audit.data.fkClientId)}
			replace
		/>
	);
}

/** Files became the read-only Data tab. */
export function LegacyFilesRedirect() {
	const { auditId } = useParams();
	return <NavigateKeepingQuery to={`/audits/${auditId}/data`} />;
}

/** Reconciliation became the analyses index. */
export function LegacyReconciliationRedirect() {
	const { auditId } = useParams();
	return <NavigateKeepingQuery to={`/audits/${auditId}/analyses`} />;
}

/** Processing merged into Analyses (2026-09-14 feedback, Task T5) — the
 *  live per-step progress it showed lives there now, alongside every
 *  analysis starting itself instead of waiting on a click. This app was open
 *  in front of a user when the tab moved, so the old URL redirects rather
 *  than 404ing. */
export function LegacyProcessingRedirect() {
	const { auditId } = useParams();
	return <NavigateKeepingQuery to={`/audits/${auditId}/analyses`} />;
}
